import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { JobLevel } from '~/shared/data-contract/job-level';
import type { JobLevelDetails } from '~/shared/data-contract/job-level-details';
import type { JobRole } from '~/shared/data-contract/job-role';
import type { JobRoleDetails } from '~/shared/data-contract/job-role-details';

import type { AssignReviewerItem } from '../../fetchers/job-architecture/job-architecture-fetchers';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { updateLevelReviewerData, updateRoleReviewerData } from './cache';
import { jobArchitectureKeys } from './query-keys';

type UpdateArguments = {
    readonly clusterId: string;
    readonly familyId?: string;
    readonly item: AssignReviewerItem;
    readonly updateType: 'create' | 'update';
};

export type UseAssignReviewerResult = UseMutationResult<JobLevel | JobRole, Error, UpdateArguments>;

type JobItem = JobLevel | JobLevelDetails | JobRole | JobRoleDetails;

export function createAssignReviewerItem<T extends JobItem>(
    item: T,
    reviewer: {
        readonly id: string;
        readonly firstName: string;
        readonly lastName: string;
        readonly state: 'approved' | 'assigned';
    },
    itemType: T extends JobLevel | JobLevelDetails ? 'job-level' : 'job-role',
): AssignReviewerItem {
    return {
        ...item,
        reviewer,
        type: itemType,
    } as AssignReviewerItem;
}

export const useAssignReviewer = (): UseAssignReviewerResult => {
    const { assignReviewer } = useApplicationFetchers();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ item }) => {
            const result = await assignReviewer({ item });

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        onMutate: async (jobItem) => {
            if (jobItem.item.type === 'job-role' && jobItem.item.reviewer !== undefined) {
                await queryClient.cancelQueries({
                    queryKey: jobArchitectureKeys.rolesByCluster(jobItem.item.architectureId, jobItem.clusterId),
                });

                await queryClient.cancelQueries({
                    queryKey: jobArchitectureKeys.role(jobItem.item.architectureId, jobItem.item.id),
                });

                updateRoleReviewerData(jobItem.item, queryClient);
            }

            if (jobItem.item.type === 'job-level' && jobItem.item.reviewer !== undefined) {
                await queryClient.cancelQueries({
                    queryKey: jobArchitectureKeys.levelsByRole(jobItem.item.architectureId, jobItem.item.id),
                });

                updateLevelReviewerData(jobItem.item, queryClient);
            }
        },
        onSuccess: (_, { item: jobItem }) => {
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.architecture(jobItem.architectureId),
            });
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.architectureDetail(jobItem.architectureId),
            });
        },
    });
};
